import { useQuery } from '@tanstack/react-query';
import { type ReactElement } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { ApiError, apiErrorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { AppShell } from '../components/AppShell';
import { StatusBadge } from '../components/StatusBadge';
import { fetchApplication } from '../lib/bag-api';
import {
  fetchAdmissionAcknowledgement,
  fetchApplicationAdmission,
} from '../lib/registration-api';

export function AdmissionPage(): ReactElement {
  const { applicationId = '' } = useParams();
  const { token, loading } = useAuth();

  const admissionQuery = useQuery({
    queryKey: ['admission', applicationId],
    queryFn: () => fetchApplicationAdmission(applicationId),
    enabled: Boolean(token && applicationId),
    retry: false,
  });

  const acknowledgementQuery = useQuery({
    queryKey: ['admission-ack', admissionQuery.data?.id],
    queryFn: () => fetchAdmissionAcknowledgement(admissionQuery.data!.id),
    enabled: Boolean(admissionQuery.data?.id),
  });

  const applicationQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => fetchApplication(applicationId),
    enabled: Boolean(token && applicationId),
  });

  if (!loading && !token) return <Navigate to="/login" replace />;
  if (!applicationId) return <Navigate to="/dashboard" replace />;

  const admission = admissionQuery.data;
  const ack = acknowledgementQuery.data;
  const documentsOptional = applicationQuery.data?.documentsPolicy === 'OPTIONAL';

  return (
    <AppShell width="wide" title="Admission">
      <div className="page-header">
        <p className="lede">Your child&apos;s enrolment and admission status.</p>
      </div>

      {admissionQuery.isLoading ? <p className="text-muted">Loading admission…</p> : null}

      {admissionQuery.error ? (
        <div className="error-banner" role="alert">
          {admissionQuery.error instanceof ApiError
            ? apiErrorMessage(admissionQuery.error.problem)
            : 'Admission not found yet'}
        </div>
      ) : null}

      {admission ? (
        <div className="stack-lg">
          <div className="card card-flush">
            <div className="card-body stack">
              <div className="row-between">
                <h2 className="h3">Enrolment</h2>
                <StatusBadge
                  status={admission.status === 'CONFIRMED' ? 'ADMISSION_CONFIRMED' : 'ADMISSION_PROVISIONAL'}
                />
              </div>
              <dl className="learner-meta">
                <div>
                  <dt>Enrolment number</dt>
                  <dd>{admission.enrolmentNumber}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    {admission.status === 'CONFIRMED' ? 'Admission confirmed' : 'Admission fee paid'}
                  </dd>
                </div>
              </dl>
              {admission.status === 'PROVISIONAL' ? (
                <p className="text-muted text-sm">
                  {documentsOptional
                    ? 'Your admission fee is paid and your place is reserved. Document upload is not required for this application.'
                    : 'Your admission fee is paid and your place is confirmed. Upload required documents when asked.'}
                </p>
              ) : null}
            </div>
          </div>

          {ack ? (
            <div className="card card-flush">
              <div className="card-body stack">
                <h2 className="h3">Acknowledgement</h2>
                <p className="text-muted text-sm">{ack.disclaimer}</p>
                {ack.provisionalAt ? (
                  <p className="text-sm">
                    Admission fee paid: {new Date(ack.provisionalAt).toLocaleDateString('en-IN')}
                  </p>
                ) : null}
                {ack.confirmedAt ? (
                  <p className="text-sm">
                    Admission confirmed: {new Date(ack.confirmedAt).toLocaleDateString('en-IN')}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Document-upload module parked.
          {documentsOptional ? null : (
            <Link className="btn btn-secondary" to={`/applications/${applicationId}/documents`}>
              Manage documents
            </Link>
          )}
          */}
        </div>
      ) : null}
    </AppShell>
  );
}
